import { useAuth } from './useAuth';
import { useCallback } from 'react';
import { normalizePlanId, PRICING, CREDIT_COSTS } from '@/config/pricing';

type Feature = 
  | 'funds' | 'lp-portal' | 'documents' | 'reports' 
  | 'kpis' | 'cap-table' | 'api-keys'
  | 'web-search' | 'research' | 'discovery'
  | 'waterfall' | 'multi-currency' | 'founder-updates';

type UsageMetric = 'entities' | 'alerts' | 'credits';

const GROWTH_FEATURES: Feature[] = [
  'funds', 'lp-portal', 'documents', 'reports', 'kpis', 'cap-table', 
  'api-keys', 'web-search', 'research', 'discovery', 'waterfall', 
  'multi-currency', 'founder-updates'
];

export function usePlanGate() {
  const { org } = useAuth();
  const rawTier = (org?.subscription_tier || org?.plan || 'free') as string;
  const tier = normalizePlanId(rawTier);
  const planConfig = PRICING[tier];

  const canAccess = useCallback((feature: Feature): boolean => {
    if (tier === 'fund') return true;
    if (tier === 'growth') return true;
    return !GROWTH_FEATURES.includes(feature);
  }, [tier]);

  const getLimit = useCallback((metric: UsageMetric): number => {
    if (metric === 'entities') return planConfig.limits.entities;
    if (metric === 'alerts') return planConfig.limits.alerts;
    if (metric === 'credits') return planConfig.credits;
    return 0;
  }, [planConfig]);

  const isLimitReached = useCallback((metric: UsageMetric, currentUsage: number): boolean => {
    const limit = getLimit(metric);
    if (limit === -1) return false;
    return currentUsage >= limit;
  }, [getLimit]);

  const getCreditsNeeded = useCallback((action: keyof typeof CREDIT_COSTS): number => {
    return CREDIT_COSTS[action];
  }, []);

  const tierLabel = planConfig.name;

  return { canAccess, getLimit, isLimitReached, getCreditsNeeded, tier, tierLabel, planConfig };
}
